import _ from "lodash";
import { useParams } from "react-router-dom";

import useService from "../../../../../hooks/useService";
import { getModalHandler } from "../../../Common/Modals/hooks/useModalSingleton";
import {
  buildRawAddress,
  deleteNestedValue,
  getDeepDiff,
  isUnchanged,
  setNestedValue,
  splitCollectionDiff,
  validateProviderNpi,
} from "../helper/functions";
import { useProviderDetailStore } from "../store/provider-details/providerDetailStore";

import useFetchProviderDetails from "./useFetchProviderDetails";

export default function useModifyProviderDetails() {
  const providerRepository = useService("repositories/provider-repository");
  const providerEmailRepository = useService("repositories/provider-email-repository");
  const providerContactInfoRepository = useService("repositories/provider-contact-info-repository");
  const addressRepository = useService("repositories/address-repository");

  const { id } = useParams();
  const { openSuccessModal, openFailureModal } = getModalHandler();
  const { fetchProviderDetails } = useFetchProviderDetails();

  const {
    initialData,
    modifiedData,
    setModifiedData,
    setDisableCancelButton,
    setDisableSaveButton,
    setOpenModal,
  } = useProviderDetailStore();

  const updateState = (updated) => {
    const unchanged = isUnchanged(initialData, updated);
    setDisableCancelButton(unchanged);
    setDisableSaveButton(unchanged);
    setModifiedData(updated);
  };

  const handleOnClickEditButton = () => setOpenModal(true);

  const handleOnClickCancelButton = () => {
    setOpenModal(false);
    setModifiedData(initialData);
    setDisableCancelButton(true);
    setDisableSaveButton(true);
  };

  const handleFieldOnChangeValue = (field, value) => {
    updateState({ ...modifiedData, [field]: value });
  };

  const handleProviderTypeChange = handleFieldOnChangeValue;

  const handleAdditionalEmailOnChange = (path, value) => {
    const updated = _.cloneDeep(modifiedData);
    updateState(setNestedValue(updated, path, value));
  };

  const handleAdditionalEmailDelete = (path, emailType) => {
    const updated = _.cloneDeep(modifiedData);
    deleteNestedValue(updated, path);
    updated.emailOrder = updated.emailOrder.filter((t) => t !== emailType);
    updateState(updated);
  };

  const handleAdditionalPhoneDelete = (path, contactLabel) => {
    const updated = _.cloneDeep(modifiedData);
    deleteNestedValue(updated, path);
    updated.contactInfoOrder = updated.contactInfoOrder.filter((t) => t !== contactLabel);
    updateState(updated);
  };

  const updateAddress = async (address) => {
    try {
      const raw = buildRawAddress(address);
      await addressRepository.patch(address.id, {
        ...address,
        raw,
        formatted: raw,
      });
    } catch (err) {
      console.error(err);
      openFailureModal(err);
    }
  };

  const handleOnClickSaveButton = async () => {
    try {
      const npiError = validateProviderNpi(modifiedData?.npi);

      if (npiError) {
        openFailureModal({ message: npiError });
        return;
      }

      const diff = getDeepDiff(modifiedData, initialData);
      if (!diff) {
        return;
      }

      const payload = Object.fromEntries(
        [
          "entity",
          "npi",
          "nps",
          "homepage",
          "activeState",
          "email",
          "communicationPreference",
          "providerTypes",
          "address",
          "phone",
          "notes",
          "name",
          "inNetwork",
          "acceptsFunding",
          "receivingClients",
          "rating",
        ]
          .filter((key) => diff[key] !== undefined)
          .map((key) => [key, modifiedData[key]])
      );

      if (diff.providerTypes) {
        payload.providerTypes = modifiedData.providerTypes.map((t) => t.id);
      }

      if (diff.address) {
        await updateAddress(modifiedData.address);
      }

      if (Object.keys(payload).length) {
        await providerRepository.patch(id, payload);
      }

      if (diff.emails) {
        const { update, create, remove } = splitCollectionDiff(
          modifiedData.emails,
          initialData.emails,
          (old, curr) => ({ id: old.id, email: curr.email }),
          (key, curr) => ({
            emailType: Number(key),
            email: curr.email,
            provider: id,
          })
        );

        update.length && (await providerEmailRepository.bulkUpdate(update));
        create.length && (await providerEmailRepository.bulkCreate(create));
        remove.length && (await providerEmailRepository.bulkDelete(remove));
      }

      if (diff.contactInfos) {
        const { update, create, remove } = splitCollectionDiff(
          modifiedData.contactInfos,
          initialData.contactInfos,
          (old, curr) => ({ id: old.id, display: curr.display }),
          (key, curr) => ({
            contactLabel: Number(key),
            display: curr.display,
            related_provider: id,
          })
        );

        update.length && (await providerContactInfoRepository.bulkUpdate(update));
        create.length && (await providerContactInfoRepository.bulkCreate(create));
        remove.length && (await providerContactInfoRepository.bulkDelete(remove));
      }

      setOpenModal(false);
      openSuccessModal();
      fetchProviderDetails();
    } catch (err) {
      console.error(err);
      openFailureModal(err);
    }
  };

  return {
    handleFieldOnChangeValue,
    handleProviderTypeChange,
    handleAdditionalEmailOnChange,
    handleAdditionalEmailDelete,
    handleAdditionalPhoneDelete,
    handleOnClickEditButton,
    handleOnClickSaveButton,
    handleOnClickCancelButton,
  };
}
